import React, { useState, useEffect } from "react";

import { X } from "lucide-react";

import Button from "../../../../Components/Button";
import CommonInput from "../../../../Components/Input";
import CommonModal from "../../../../Components/Modal";
import Select from "../../../../Components/Select";
import TextArea from "../../../../Components/TextArea";
import { useAppDispatch, useAppSelector } from "../../../../Store/Hooks";
import {
  fetchOrganizationLevels,
  createAssessmentTemplate as createAssessmentTemplateFromAdmin,
  updateAssessmentTemplateAdmin,
} from "../../../../Store/Reducers/AdminSlice";
import {
  createAssessmentTemplate as createAssessmentTemplateFromAssessment,
  updateAssessmentTemplate as updateAssessmentTemplateFromAssessment,
  CreateTemplateRequest,
} from "../../../../Store/Reducers/AssessmentSlice";
import { normalizeTeamAssessmentDisplayName } from "../../../../Utils/Helper/assessmentOriginHelpers";
import { getAccountLevelCompactLabel } from "../../../../Utils/Helper/levelUtils";
import { toast } from "../../../../Utils/Helper/toast";
import { Template, Question } from "../TemplateList";

interface TemplateFormProps {
  isOpen: boolean;
  onClose: () => void;
  template?: Template | null;
  activeTab: "izba_provided" | "custom";
  onSuccess?: () => void;
  isSuperAdmin?: boolean;
}

const TemplateForm: React.FC<TemplateFormProps> = ({
  isOpen,
  onClose,
  template,
  activeTab,
  onSuccess,
  isSuperAdmin = false,
}) => {
  const dispatch = useAppDispatch();
  const isEditMode = !!template;

  // Get organization levels from AdminSlice
  const { organizationLevels, assessmentTemplates: adminTemplates } =
    useAppSelector((state: any) => state.admin);
  const { templates: reduxTemplates } = useAppSelector(
    state => state.assessment
  );

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    type: "",
    isSelfAssessment: false,
  });

  const [questions, setQuestions] = useState<Question[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedBaseTemplate, setSelectedBaseTemplate] =
    useState<Template | null>(null);
  const [izbaProvidedTemplates, setIzbaProvidedTemplates] = useState<
    Template[]
  >([]);
  const [errors, setErrors] = useState<{
    name?: string;
    type?: string;
    questions?: string;
    selfAssessment?: string;
  }>({});

  const CHOICE_QUESTION_TYPES = ["dropdown", "multiple_choice", "scale"];
  const getTemplateId = (t: any) => t?.template_id || t?.id;
  const canAutoManageShortQuestions = !selectedBaseTemplate && !isEditMode;
  const isChoiceQuestionType = (type: string) =>
    CHOICE_QUESTION_TYPES.includes(type);

  const getInitialQuestion = (): Question => ({
    id: `q_${Date.now()}`,
    question: "",
    type: "textarea",
    required: false,
    options: null,
    order_index: 1,
  });

  const assessmentTemplatesData = React.useMemo(() => {
    if (!reduxTemplates) return [];
    return (
      reduxTemplates?.templates ||
      reduxTemplates?.data?.templates ||
      (Array.isArray(reduxTemplates) ? reduxTemplates : [])
    );
  }, [reduxTemplates]);

  const templatesData = React.useMemo(() => {
    return isSuperAdmin
      ? Array.isArray(adminTemplates)
        ? adminTemplates
        : []
      : assessmentTemplatesData;
  }, [isSuperAdmin, adminTemplates, assessmentTemplatesData]);

  const isSelfAssessmentTemplate = (templateData: any): boolean => {
    const metadataCategories =
      templateData?.json_schema?.metadata?.categories ||
      templateData?.data?.metadata?.categories ||
      templateData?.metadata?.categories ||
      templateData?.categories ||
      [];

    return Array.isArray(metadataCategories)
      ? metadataCategories.includes("self_assessment")
      : false;
  };

  const existingSelfAssessmentTemplate = React.useMemo(() => {
    if (!Array.isArray(templatesData)) return null;

    return (
      templatesData.find((t: any) => {
        const templateId = getTemplateId(t);
        if (isEditMode && template && templateId === template.id) {
          return false;
        }
        return isSelfAssessmentTemplate(t);
      }) || null
    );
  }, [templatesData, isEditMode, template]);

  // Load organization levels
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchOrganizationLevels() as any);
    }
  }, [dispatch, isOpen]);

  // Generate default questions for short assessment
  const generateDefaultShortQuestions = (): Question[] => {
    const maturityOptions =
      organizationLevels && organizationLevels.length > 0
        ? organizationLevels.map((level: any) => ({
            label: getAccountLevelCompactLabel(
              level.level_code || level.levels,
              level.name
            ),
            value: level.level_code || level.levels,
          }))
        : [
            { label: "AL - 1 Foundation", value: "L1" },
            { label: "AL - 2 Development", value: "L2" },
            { label: "AL - 3 Optimization", value: "L3" },
            { label: "AL - 4 Advanced", value: "L4" },
            { label: "AL - 5 Excellence", value: "L5" },
            { label: "AL - 6 Innovation", value: "L6" },
          ];

    return [
      {
        id: "maturity_level",
        question:
          "What is your assessment of the current maturity level for this account?",
        type: "dropdown",
        required: true,
        options: maturityOptions,
        order_index: 1,
      },
      {
        id: "rationale",
        question: "Provide rationale/evidence for your assessment",
        type: "textarea",
        required: true,
        options: null,
        order_index: 2,
      },
    ];
  };

  // Handle template selection for creating new template
  const handleTemplateSelection = (templateId: string) => {
    if (!templateId) {
      setSelectedBaseTemplate(null);
      setFormData(prev => ({
        ...prev,
        name: "",
        description: "",
        type: "",
        isSelfAssessment: false,
      }));
      // Add default empty question when clearing base template
      setQuestions([getInitialQuestion()]);
      return;
    }

    const selectedTemplate = izbaProvidedTemplates.find(
      t => t.id === templateId
    );
    if (selectedTemplate) {
      setSelectedBaseTemplate(selectedTemplate);
      setFormData(prev => ({
        ...prev,
        name: selectedTemplate.name,
        description: selectedTemplate.description,
        type: selectedTemplate.category === "short" ? "short" : "long",
      }));
      setQuestions(
        selectedTemplate.questions.map(q => ({
          ...q,
          id:
            q.id ||
            `q_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        }))
      );
    }
  };

  // Update izba provided templates when redux templates change
  useEffect(() => {
    if (reduxTemplates && !isSuperAdmin) {
      const templatesData =
        reduxTemplates?.templates ||
        reduxTemplates?.data?.templates ||
        (Array.isArray(reduxTemplates) ? reduxTemplates : []);

      const izbaTemplates = (Array.isArray(templatesData) ? templatesData : [])
        .filter((t: any) => {
          const template_type =
            t.is_global === true ? "izba_provided" : "custom";
          return template_type === "izba_provided";
        })
        .map((t: any) => ({
          id: t.template_id || t.id,
          name: t.name || "",
          description: t.description || "",
          category: t.type || "short",
          template_type: "izba_provided" as const,
          is_active: t.is_active !== undefined ? t.is_active : true,
          is_published: t.is_active !== undefined ? t.is_active : true,
          version: t.version || 1,
          created_at: t.created_at || "",
          created_by:
            typeof t.created_by === "string"
              ? t.created_by
              : t.created_by?.id || "",
          organization_id: null,
          questions: (t.json_schema?.questions || t.questions || []).map(
            (q: any) => ({
              id: q.id,
              question: q.question_text || q.question || "",
              type:
                q.question_type === "free_text"
                  ? "textarea"
                  : q.question_type === "single_choice"
                    ? "dropdown"
                    : q.question_type === "multiple_choice"
                      ? "multiple_choice"
                      : "scale",
              required: q.required !== undefined ? q.required : true,
              options: q.options?.choices
                ? q.options.choices.map((choice: any) => ({
                    value: choice.value,
                    label: choice.label,
                  }))
                : null,
              order_index: q.order_index || 0,
            })
          ),
        }));
      setIzbaProvidedTemplates(izbaTemplates);
    }
  }, [reduxTemplates, isSuperAdmin]);

  useEffect(() => {
    if (template && isOpen) {
      const isSelf = !!templatesData.find((t: any) => {
        const templateId = getTemplateId(t);
        return templateId === template.id && isSelfAssessmentTemplate(t);
      });
      setFormData({
        name: isSelf
          ? normalizeTeamAssessmentDisplayName(template.name)
          : template.name,
        description: template.description,
        type: template.category === "short" ? "short" : "long",
        isSelfAssessment: isSelf,
      });
      setQuestions(template.questions || []);
      setSelectedBaseTemplate(null);
    } else if (isOpen && !isEditMode) {
      // Reset form for new template
      setFormData({
        name: "",
        description: "",
        type: "",
        isSelfAssessment: false,
      });
      setSelectedBaseTemplate(null);
      // Add one default empty question when creating new template (will be replaced if base template is selected)
      setQuestions([getInitialQuestion()]);
    }
  }, [template, isOpen, isEditMode, templatesData]);

  // Add default questions when organization levels are loaded and form is open for new short template
  useEffect(() => {
    const shouldLoadDefaultShortQuestions =
      isOpen &&
      !isEditMode &&
      !template &&
      formData.type === "short" &&
      questions.length === 0 &&
      !selectedBaseTemplate &&
      organizationLevels &&
      organizationLevels.length > 0;

    if (shouldLoadDefaultShortQuestions) {
      setQuestions(generateDefaultShortQuestions());
    }
  }, [
    organizationLevels,
    isOpen,
    isEditMode,
    formData.type,
    selectedBaseTemplate,
    template,
  ]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleTypeChange = (e: any) => {
    const newType = e.target.value as "" | "short" | "long";
    setFormData(prev => ({
      ...prev,
      type: newType,
      isSelfAssessment: newType !== "short" ? false : prev.isSelfAssessment,
    }));

    // If changing to short and no questions exist, add default questions
    if (newType === "short" && canAutoManageShortQuestions) {
      // Wait for organization levels to load
      setTimeout(() => {
        setQuestions(generateDefaultShortQuestions());
      }, 100);
    } else if (newType === "long" && canAutoManageShortQuestions) {
      // Clear questions for long assessment if no base template
      setQuestions([]);
    } else if (newType === "") {
      // Clear questions if type is unselected
      setQuestions([]);
    }
  };

  const handleSelfAssessmentToggle = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const isChecked = e.target.checked;

    setFormData(prev => ({
      ...prev,
      isSelfAssessment: isChecked,
      type: isChecked ? "short" : prev.type,
      name: isChecked
        ? normalizeTeamAssessmentDisplayName(
            prev.name || "Team Assessment",
            "Team Assessment"
          )
        : prev.name,
    }));

    if (errors.selfAssessment) {
      setErrors(prev => ({ ...prev, selfAssessment: undefined }));
    }

    if (isChecked && canAutoManageShortQuestions) {
      setQuestions(generateDefaultShortQuestions());
    }
  };

  // Check if a question is read-only (maturity_level or rationale)
  const isQuestionReadOnly = (questionId: string): boolean => {
    return questionId === "maturity_level" || questionId === "rationale";
  };

  // Normalize options into a plain array for rendering/editing
  const getQuestionOptionsArray = (
    q: Question
  ): Array<{ label: string; value: string }> => {
    if (!q.options) return [];
    if (Array.isArray(q.options)) return q.options;
    const choices = (q.options as any)?.choices;
    return Array.isArray(choices) ? choices : [];
  };

  const handleAddQuestion = () => {
    const newQuestion: Question = {
      id: `q${Date.now()}`,
      question: "",
      type: "textarea",
      required: true,
      options: null,
      order_index: questions.length + 1,
    };
    setQuestions(prev => [...prev, newQuestion]);
  };

  const handleQuestionChange = (
    index: number,
    field: keyof Question,
    value: any
  ) => {
    const question = questions[index];
    // Prevent modification of read-only questions
    if (question && isQuestionReadOnly(question.id)) {
      return;
    }
    setQuestions(prev =>
      prev.map((q, i) => (i === index ? { ...q, [field]: value } : q))
    );
  };

  const handleRemoveQuestion = (index: number) => {
    const question = questions[index];
    // Prevent deletion of read-only questions
    if (question && isQuestionReadOnly(question.id)) {
      return;
    }
    setQuestions(prev => prev.filter((_, i) => i !== index));
  };

  const mapQuestionTypeToAPI = (type: string): string => {
    switch (type) {
      case "text":
      case "textarea":
        return "free_text";
      case "dropdown":
        return "single_choice";
      case "multiple_choice":
        return "multiple_choice";
      case "scale":
        return "scale";
      default:
        return "free_text";
    }
  };

  const transformTemplatePayload = (payload: CreateTemplateRequest): any => {
    // Transform questions
    const transformQuestion = (q: any, index: number) => {
      const baseQuestion: any = {
        id: q.id || `q${index + 1}`,
        order_index: q.display_order ?? index + 1,
        question_text: q.question,
        question_type: q.questionType || mapQuestionTypeToAPI(q.type),
        required: q.required,
      };

      if (
        q.questionType === "free_text" ||
        (!q.questionType && (q.type === "textarea" || q.type === "text"))
      ) {
        baseQuestion.options = {
          min_length: 1,
          max_length: 500,
        };
      } else if (
        (q.questionType === "single_choice" ||
          q.questionType === "multiple_choice" ||
          q.questionType === "scale" ||
          isChoiceQuestionType(q.type)) &&
        q.options &&
        q.options.length > 0
      ) {
        baseQuestion.options = {
          choices: q.options.map((opt: any) => ({
            label: opt.label,
            value: opt.value,
          })),
          allow_multiple:
            q.questionType === "multiple_choice" ||
            q.type === "multiple_choice",
        };
      }

      return baseQuestion;
    };

    const transformedPayload: any = {
      name: payload.name,
      questionnaire_type: payload.questionnaire_type,
      description: payload.description || "",
      is_active: true,
    };
    const isSelfAssessmentPayload =
      payload.categories?.includes("self_assessment");
    transformedPayload.data = {
      metadata: {
        categories: payload.categories || [],
        ...(isSelfAssessmentPayload
          ? { template_kind: "self_assessment" }
          : {}),
      },
      questions: payload.questions.map((q, idx) => transformQuestion(q, idx)),
    };

    return transformedPayload;
  };

  const handleSubmit = async () => {
    const newErrors: typeof errors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Template name is required";
    }

    if (!formData.type) {
      newErrors.type = "Template type is required";
    }

    if (questions.length === 0) {
      newErrors.questions = "At least one question is required";
    }

    if (formData.isSelfAssessment && existingSelfAssessmentTemplate) {
      newErrors.selfAssessment =
        "Only one Team Assessment template can be created.";
    }

    // Validate questions
    const invalidQuestions = questions.some(
      q =>
        !q.question.trim() ||
        (isChoiceQuestionType(q.type) && (!q.options || q.options.length === 0))
    );

    if (invalidQuestions) {
      newErrors.questions = "Please fill all question fields and options";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      if (newErrors.selfAssessment) {
        toast.error(newErrors.selfAssessment);
      }
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: CreateTemplateRequest = {
        name: formData.isSelfAssessment
          ? normalizeTeamAssessmentDisplayName(
              formData.name.trim(),
              "Team Assessment"
            )
          : formData.name.trim(),
        description: formData.description.trim() || undefined,
        categories: formData.isSelfAssessment ? ["self_assessment"] : [],
        questionnaire_type: formData.isSelfAssessment
          ? "short"
          : formData.type || "",
        questions: questions.map((q, index) => ({
          id: q.id,
          question: q.question.trim(),
          questionType: mapQuestionTypeToAPI(q.type),
          required: q.required,
          options:
            isChoiceQuestionType(q.type) && q.options && q.options.length > 0
              ? q.options
              : null,
          display_order: index + 1,
        })),
      };

      if (isEditMode && template) {
        if (isSuperAdmin) {
          const transformedPayload = transformTemplatePayload(payload);
          await dispatch(
            updateAssessmentTemplateAdmin({
              id: template.id,
              data: transformedPayload,
            })
          ).unwrap();
        } else {
          await dispatch(
            updateAssessmentTemplateFromAssessment({
              id: template.id,
              data: payload,
            })
          ).unwrap();
        }
      } else {
        const transformedPayload = transformTemplatePayload(payload);
        // Use AdminSlice for super admin, AssessmentSlice for others
        if (isSuperAdmin) {
          await dispatch(
            createAssessmentTemplateFromAdmin(transformedPayload)
          ).unwrap();
        } else {
          await dispatch(
            createAssessmentTemplateFromAssessment(transformedPayload)
          ).unwrap();
        }
      }

      toast.success(
        `Template ${isEditMode ? "updated" : "created"} successfully!`
      );
      onSuccess?.();
      onClose();
    } catch (error: any) {
      const errorMessage =
        error?.message ||
        `Failed to ${isEditMode ? "update" : "create"} template. Please try again.`;
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <CommonModal
      isOpen={isOpen}
      toggle={onClose}
      title={isEditMode ? "Edit Template" : "Create New Template"}
      size="4xl"
    >
      <div className="template-form">
        <div className="space-y-4">
          {isSuperAdmin && (
            <>
              <div className="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-3 bg-gray-50">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Team Assessment
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={formData.isSelfAssessment}
                  onChange={handleSelfAssessmentToggle}
                  className="h-5 w-5 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
              </div>
              {errors.selfAssessment && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.selfAssessment}
                </p>
              )}
            </>
          )}

          {/* Template Selection for Non-SuperAdmin */}
          {!isSuperAdmin && !isEditMode && (
            <div className="mb-6">
              <Select
                label={"Select Base Template (Optional)"}
                name="baseTemplate"
                value={selectedBaseTemplate?.id || ""}
                onChange={e => handleTemplateSelection(e.target.value)}
                options={[
                  { label: "Start from scratch (no base template)", value: "" },
                  ...izbaProvidedTemplates.map(template => ({
                    label: `${template.name} (${template.category})`,
                    value: template.id,
                  })),
                ]}
                isShowPlaceHolder={false}
                infoText={`Choose from Izba Provided templates only (${izbaProvidedTemplates.length} available)`}
              />
              {selectedBaseTemplate && (
                <p className="text-sm text-gray-600 mt-2">
                  Selected: {selectedBaseTemplate.name} -{" "}
                  {selectedBaseTemplate.description}
                  <br />
                  <span className="text-xs text-blue-600">
                    Questions from this template will be loaded
                  </span>
                </p>
              )}
            </div>
          )}

          {/* Basic Info */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-6">
              <CommonInput
                label="Template Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter template name"
                required
                error={errors.name}
                className="flex-1"
              />

              <Select
                label="Type"
                name="type"
                value={formData.type}
                onChange={handleTypeChange}
                required
                error={errors.type}
                options={[
                  { label: "Short Assessment", value: "short" },
                  { label: "Long Assessment", value: "long" },
                ]}
                placeholder="Select Type"
                isShowPlaceHolder={true}
                className="flex-1"
              />
              {formData.type === "short" &&
                questions.length === 0 &&
                !selectedBaseTemplate && (
                  <p className="text-xs text-gray-500 mt-1">
                    Default questions (maturity level and rationale) will be
                    added automatically
                  </p>
                )}
            </div>

            <TextArea
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter description"
            />
          </div>

          {/* Questions Section */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Questions</h3>
              <Button
                variant="clear"
                onClick={handleAddQuestion}
                className="text-sm primary-color"
              >
                + Add Question
              </Button>
            </div>

            {errors.questions && (
              <p className="text-red-500 text-sm mb-2">{errors.questions}</p>
            )}

            <div className="space-y-4 max-h-96 overflow-y-auto">
              {questions.map((question, index) => {
                const isReadOnly = isQuestionReadOnly(question.id);
                return (
                  <div
                    key={question.id}
                    className={`border border-gray-200 rounded-lg p-4 ${
                      isReadOnly ? "opacity-75 bg-gray-50" : ""
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">
                          Question {index + 1}
                        </span>
                        {isReadOnly && (
                          <span className="text-xs text-blue-600 font-medium">
                            (Read-only)
                          </span>
                        )}
                      </div>
                      {!isReadOnly && (
                        <Button
                          variant="clear"
                          onClick={() => handleRemoveQuestion(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>

                    <div className="space-y-3">
                      <CommonInput
                        label="Question Text"
                        name={`question-${index}`}
                        value={question.question}
                        onChange={e =>
                          handleQuestionChange(
                            index,
                            "question",
                            e.target.value
                          )
                        }
                        placeholder="Enter question text"
                        required
                        disabled={isReadOnly}
                        className="mb-6"
                      />

                      <Select
                        label="Question Type"
                        name={`question-type-${index}`}
                        value={question.type}
                        onChange={e => {
                          const newType = e.target.value;
                          handleQuestionChange(index, "type", newType);
                          // Initialize options for dropdown/multiple_choice/scale
                          if (
                            (newType === "dropdown" ||
                              newType === "multiple_choice" ||
                              newType === "scale") &&
                            getQuestionOptionsArray(question).length === 0
                          ) {
                            handleQuestionChange(index, "options", [
                              { label: "", value: "" },
                              { label: "", value: "" },
                            ]);
                          }
                        }}
                        options={[
                          { label: "Free Text", value: "textarea" },
                          { label: "Single Choice", value: "dropdown" },
                          {
                            label: "Multiple Choice",
                            value: "multiple_choice",
                          },
                          { label: "Scale", value: "scale" },
                        ]}
                        isShowPlaceHolder={false}
                        disabled={isReadOnly}
                        className="mb-6"
                      />

                      {(question.type === "dropdown" ||
                        question.type === "multiple_choice" ||
                        question.type === "scale") && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Options
                          </label>
                          <div className="space-y-2">
                            {getQuestionOptionsArray(question).map(
                              (option, optIndex) => (
                                <div
                                  key={optIndex}
                                  className="flex items-center gap-2"
                                >
                                  <CommonInput
                                    name={`Option label${optIndex}`}
                                    value={option.label}
                                    onChange={e => {
                                      const newOptions = [
                                        ...getQuestionOptionsArray(question),
                                      ];
                                      newOptions[optIndex] = {
                                        ...option,
                                        label: e.target.value,
                                      };
                                      handleQuestionChange(
                                        index,
                                        "options",
                                        newOptions
                                      );
                                    }}
                                    placeholder="Option label"
                                    className="flex-1"
                                    disabled={isReadOnly}
                                  />
                                  <CommonInput
                                    name={`Option value ${optIndex}`}
                                    value={option.value}
                                    onChange={e => {
                                      const newOptions = [
                                        ...getQuestionOptionsArray(question),
                                      ];
                                      newOptions[optIndex] = {
                                        ...option,
                                        value: e.target.value,
                                      };
                                      handleQuestionChange(
                                        index,
                                        "options",
                                        newOptions
                                      );
                                    }}
                                    placeholder="Option value"
                                    className="flex-1"
                                    disabled={isReadOnly}
                                  />
                                  {!isReadOnly && (
                                    <Button
                                      variant="clear"
                                      onClick={() => {
                                        const newOptions =
                                          getQuestionOptionsArray(
                                            question
                                          ).filter((_, i) => i !== optIndex);
                                        handleQuestionChange(
                                          index,
                                          "options",
                                          newOptions
                                        );
                                      }}
                                      className="text-red-500"
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              )
                            )}
                            {!isReadOnly && (
                              <Button
                                variant="clear"
                                onClick={() => {
                                  const newOptions = [
                                    ...getQuestionOptionsArray(question),
                                    { label: "", value: "" },
                                  ];
                                  handleQuestionChange(
                                    index,
                                    "options",
                                    newOptions
                                  );
                                }}
                                className="text-sm primary-color"
                              >
                                + Add Option
                              </Button>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id={`required-${index}`}
                          checked={question.required}
                          onChange={e =>
                            handleQuestionChange(
                              index,
                              "required",
                              e.target.checked
                            )
                          }
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                          disabled={isReadOnly}
                        />
                        <label
                          htmlFor={`required-${index}`}
                          className="ml-2 text-sm text-gray-700"
                        >
                          Required
                        </label>
                      </div>
                    </div>
                  </div>
                );
              })}

              {questions.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No questions added. Click {"`Add Question`"} to get started.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Modal Actions */}
        <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
          <Button
            variant="outline-primary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={isSubmitting}
          >
            {isEditMode ? "Update Template" : "Create Template"}
          </Button>
        </div>
      </div>
    </CommonModal>
  );
};

export default TemplateForm;
